<template>
  <div data-testid="information">
    <HeroContainer class="rlp__hero-container">
      <template #navbar="{ isNavbarTransparent }">
        <NavBar class="rlp__navbar" :transparent="isNavbarTransparent">
          <template #left>
            <BackButton @click="handleGoBack" />
          </template>
        </NavBar>
      </template>
      <template #hero>
        <Hero height="80vh" :bg-color="NebraskaColors.backgroundTertiary">
          <LoaderIndicator v-if="isLoading" class="rlp__loader" size="36" />
          <HelperViewStatus
            v-else-if="hasError"
            class="rlp__helper"
            title="Não foi possível carregar as informações"
            description="Aguarde um momento e tente novamente"
            button-label="Tentar novamente"
            status="alert"
            @click="emitRefresh"
          />
          <Header
            v-else-if="pageData"
            :page-data="pageData"
            class="rlp__header"
          />
        </Hero>
      </template>
      <template #content>
        <LoaderIndicator v-if="isLoading" class="rlp__loader" size="36" />
        <HelperViewStatus
          v-else-if="hasError"
          class="rlp__helper"
          title="Não foi possível carregar as informações"
          description="Aguarde um momento e tente novamente"
          button-label="Tentar novamente"
          status="alert"
          @click="emitRefresh"
        />
        <main
          v-else-if="pageData"
          :class="['rlp__main', { 'rlp__main--enabled': isRLPEnabled }]"
        >
          <template v-if="isRLPEnabled">
            <Cancellation
              :page-data="pageData"
              class="rlp__section"
              @on-cancellation="handleRLPCancellation"
              @refresh="emitRefresh"
            />
            <div class="rlp__main__divider" />
          </template>
          <About :page-data="pageData" class="rlp__section" />
          <Benefits :page-data="pageData" class="rlp__section" />
          <Regulation />
        </main>
      </template>
    </HeroContainer>
    <Activation
      v-if="isRLPDisabled && !isLoading && pageData"
      :page-data="pageData"
      class="rlp__footer"
      @refresh="emitRefresh"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router/composables';

import {
  NavBar,
  LoaderIndicator,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { Hero, HeroContainer } from '@/modules/common/components/Hero';
import { BackButton } from '@/modules/common/components/buttons';
import { Header } from './components/Header';
import { About } from './components/About';
import { Cancellation } from './components/Cancellation';
import { Benefits } from './components/Benefits';
import { Regulation } from './components/Regulation';
import { Activation } from './components/Activation';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { RLPPageData, RLPStatus } from '@/modules/trade/services/rlp';
import { RLPPageStatus, RLPSteps } from '../../types';

interface Props {
  pageData: RLPPageData;
  pageStatus: RLPPageStatus;
}
const props = defineProps<Props>();

interface Emits {
  (e: 'change-step', step: RLPSteps): void;
  (e: 'refresh'): void;
}
const emit = defineEmits<Emits>();

const router = useRouter();

const isLoading = computed(() => {
  return props.pageStatus === RLPPageStatus.Loading;
});

const hasError = computed(() => {
  return props.pageStatus === RLPPageStatus.Error;
});

const isRLPEnabled = computed(() => {
  return props.pageData?.status === RLPStatus.Active;
});

const isRLPDisabled = computed(() => {
  return props.pageData?.status === RLPStatus.Inactive;
});

const emitRefresh = () => {
  emit('refresh');
};

const handleRLPCancellation = () => {
  emit('change-step', RLPSteps.CancellationForm);
};

const handleGoBack = () => {
  router.push({ name: 'trade' });
};
</script>

<style lang="less" scoped>
.rlp {
  &__navbar {
    color: @element-primary;
  }

  &__loader {
    margin: @size-spacing-x1400 auto;
    text-align: center;
  }

  &__helper {
    margin: @size-spacing-x1400 auto;
    text-align: center;
  }

  &__header {
    max-width: 800px;
    margin: 0 auto;
  }

  &__main {
    padding-bottom: @size-spacing-x1000;

    &--enabled {
      padding-bottom: 0;
    }

    &__divider {
      width: 100%;
      height: 1px;
      background: @divider-primary;
    }
  }

  &__section {
    max-width: calc(800px + 2 * @size-spacing-x400);
    width: 100%;
    margin: @size-spacing-x800 auto;
    padding: 0 @size-spacing-x400;

    .from-md({
      max-width: calc(800px + 2 * @size-spacing-x800);
      padding: 0 @size-spacing-x800;
    });
  }

  &__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 100;
    background: @background-secondary;
  }

  &__hero-container {
    :deep(.hero__content) {
      margin-bottom: @size-spacing-x400;
    }
  }
}
</style>
